import styled, { keyframes } from 'styled-components'
import variables from '../../styles/variables'

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`

export const Container = styled.div`
  background: linear-gradient(135deg, ${variables.colors.white} 0%, ${variables.colors.greyLightest} 100%);
  border: 2px solid ${variables.colors.greyLight};
  border-radius: 20px;
  padding: 2.5rem;
  margin: 2.5rem 0;
  box-shadow: 0 12px 40px rgba(0,0,0,0.08);
  transition: all 0.4s ease;
  animation: ${fadeInUp} 0.6s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0,0,0,0.12);
    border-color: ${variables.colors.primary};
  }
`

export const Header = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid ${variables.colors.greyLight};
`

export const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`

export const Logo = styled.div`
  img {
    height: 60px;
    max-width: 120px;
    object-fit: contain;
    filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
  }
`

export const CustomerDetails = styled.div`
  flex-grow: 1;
`

export const CustomerName = styled.h3`
  color: ${variables.colors.greyDarkest};
  font-family: ${variables.fonts.header};
  font-size: 2rem;
  margin: 0 0 0.5rem 0;
  font-weight: 700;
`

export const Industry = styled.div`
  color: ${variables.colors.primary};
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

export const Description = styled.p`
  color: ${variables.colors.greyDark};
  font-size: 1.1rem;
  line-height: 1.7;
  margin: 0;
`

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 1.5rem 0;
  
  @media ${variables.media.sm} {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
  }
`

export const MetricCard = styled.div`
  background: ${props => props.highlight ? 
    `linear-gradient(135deg, ${variables.colors.jade.darker} 0%, ${variables.colors.jade.dark} 100%)` : 
    variables.colors.white
  };
  padding: 1.5rem;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 35px rgba(0,0,0,0.15);
    animation: ${pulse} 0.6s ease;
  }
`

export const MetricValue = styled.div`
  color: ${props => props.highlight ? variables.colors.white : variables.colors.greyDarkest};
  font-size: ${props => props.highlight ? '1.8rem' : '1.5rem'};
  font-weight: 900;
  font-family: ${variables.fonts.header};
  margin-bottom: 0.5rem;
  line-height: 1;
  word-wrap: break-word;
  hyphens: auto;
  
  @media ${variables.media.sm} {
    font-size: ${props => props.highlight ? '1.5rem' : '1.3rem'};
  }
`

export const MetricLabel = styled.div`
  color: ${props => props.highlight ? variables.colors.white : variables.colors.greyDark};
  font-size: 0.75rem;
  font-weight: ${props => props.highlight ? 600 : 500};
  opacity: ${props => props.highlight ? 1 : 0.9};
  text-transform: uppercase;
  letter-spacing: 0.2px;
  line-height: 1.3;
  word-wrap: break-word;
  hyphens: auto;
  
  @media ${variables.media.sm} {
    font-size: 0.7rem;
    letter-spacing: 0.1px;
  }
`

export const AchievementsList = styled.div`
  background: ${variables.colors.oracle.lightest};
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid ${variables.colors.primary};
`

export const AchievementsTitle = styled.h4`
  color: ${variables.colors.greyDarkest};
  font-size: 1.2rem;
  margin: 0 0 1rem 0;
  font-weight: 600;
`

export const AchievementItem = styled.div`
  display: flex;
  align-items: center;
  color: ${variables.colors.greyDark};
  font-size: 1rem;
  margin-bottom: 0.75rem;
  line-height: 1.5;
  
  &:last-child {
    margin-bottom: 0;
  }
`

export const CheckIcon = styled.span`
  background: ${variables.colors.primary};
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
  margin-right: 1rem;
  flex-shrink: 0;
`

export const QuoteSection = styled.div`
  background: ${variables.colors.white};
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid ${variables.colors.greyLight};
  position: relative;
  
  &::before {
    content: '"';
    position: absolute;
    top: -10px;
    left: 20px;
    font-size: 4rem;
    color: ${variables.colors.primary};
    font-family: serif;
    line-height: 1;
  }
`

export const Quote = styled.blockquote`
  color: ${variables.colors.greyDarkest};
  font-size: 1.2rem;
  font-style: italic;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
  padding-left: 1rem;
`

export const QuoteAttribution = styled.div`
  text-align: right;
`

export const QuotedBy = styled.div`
  color: ${variables.colors.greyDarkest};
  font-weight: 600;
  font-size: 1.1rem;
`

export const QuotedByTitle = styled.div`
  color: ${variables.colors.greyDark};
  font-size: 0.95rem;
  margin-top: 0.25rem;
`